// src/components/ui/Charts/BarChart.tsx
import React, { useMemo, forwardRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart as RechartsBarChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { COLORS } from '@/theme/colors';
import { CustomTooltip } from './CustomTooltip';
import { ChartRenderer } from './ChartRenderer';
import { DATE_FORMATS, LOCALES } from '@/utils/constants';
import { formatDateTime, utcToLocal, formatSmartDate } from '@/utils/dateUtils';

export interface BarDataSeries {
  name: string;
  dataKey: string;
  color?: string;
  stackId?: string;
  itemColors?: string[];
  hidden?: boolean;
}

export interface BarChartProps {
  // Основные данные и настройки
  data: Array<Record<string, any>>;
  series: BarDataSeries[];
  xAxisKey?: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
  
  // Базовые настройки отображения (обратная совместимость)
  showGrid?: boolean;
  showLegend?: boolean;
  height?: number;
  className?: string;
  
  // Специфичные настройки для BarChart
  layout?: 'vertical' | 'horizontal';
  barSize?: number;
  borderRadius?: number;
  colorsByItem?: boolean;
  
  // Настройки данных
  valueType?: 'number' | 'percent' | 'currency' | 'compact';
  precision?: number;
  tooltipFormatter?: (value: any, name: string, props: any) => React.ReactNode;
  
  // Настройки форматирования дат
  xAxisDateFormat?: boolean;
  xAxisDateFormatType?: 'short' | 'full' | 'smart';
  dateFromUTC?: boolean;
  locale?: string;
  
  // Настройки осей и сетки
  xAxisTickLine?: boolean;
  yAxisTickLine?: boolean;
  
  // Новые параметры для унифицированного управления
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full' | 'auto';
  minHeight?: number;
  aspectRatio?: number;
  isLoading?: boolean;
  error?: string | null;
  noData?: boolean;
  title?: string;
  subtitle?: string;
  margin?: { top?: number; right?: number; bottom?: number; left?: number };
  
  // Дополнительные параметры
  useCustomIds?: boolean;
  
  // Колбэки и обработчики
  onSeriesToggle?: (dataKey: string, isActive: boolean) => void;
  onClick?: (data: any, index: number) => void;
  onBarClick?: (data: any, index: number) => void;
}

/**
 * Компонент для отображения столбчатых/полосовых диаграмм (BarChart)
 * Поддерживает унифицированное управление размерами и состояниями через ChartRenderer
 */
export const BarChart = forwardRef<HTMLDivElement, BarChartProps>(({
  // Основные данные и настройки
  data,
  series,
  xAxisKey = 'name',
  xAxisLabel,
  yAxisLabel,
  
  // Базовые настройки отображения (обратная совместимость)
  showGrid = true,
  showLegend = true,
  height,
  className = '',
  
  // Специфичные настройки для BarChart
  layout = 'horizontal',
  barSize = 20,
  borderRadius = 0,
  colorsByItem = false,
  
  // Настройки данных
  valueType = 'number',
  precision = 2,
  tooltipFormatter,
  
  // Настройки форматирования дат
  xAxisDateFormat = false,
  xAxisDateFormatType = 'short',
  dateFromUTC = true,
  locale = LOCALES.DEFAULT,
  
  // Настройки осей и сетки
  xAxisTickLine = true,
  yAxisTickLine = true,
  
  // Новые параметры для унифицированного управления
  size = 'md',
  minHeight,
  aspectRatio,
  isLoading = false,
  error = null,
  noData = false,
  title,
  subtitle,
  // Дополнительные параметры
  useCustomIds = false,
  
  // Колбэки и обработчики
  onSeriesToggle,
  onClick,
  onBarClick
}, ref) => {
  const { i18n } = useTranslation(['analytics', 'ui', 'common']);
  const currentLocale = i18n.language || locale;
  
  // Обратная совместимость для размеров
  const effectiveMinHeight = useMemo(() => {
    if (minHeight) return minHeight;
    if (height) return height;
    return undefined;
  }, [minHeight, height]);
  
  // Создаем палитру цветов на основе темы
  const defaultColors = [
    COLORS.primary,
    '#32a852',
    '#3273a8',
    '#a83232',
    '#a87832',
    '#8432a8',
  ];
  
  // Функция форматирования меток оси X для дат
  const formatXAxisTick = useMemo(() => {
    if (!xAxisDateFormat) return undefined;
    
    return (value: string): string => {
      if (!value) return value;
      
      try {
        if (dateFromUTC) {
          switch (xAxisDateFormatType) {
            case 'smart':
              return formatSmartDate(value, false, currentLocale);
            case 'full':
              return formatDateTime(value, false, currentLocale);
            case 'short':
            default:
              // Форматируем только день и месяц
              const localDateTime = utcToLocal(value, currentLocale);
              if (!localDateTime) return value;
              return localDateTime.toFormat(DATE_FORMATS.SHORT_DATE);
          }
        } else {
          // Для дат не в UTC формате применяем стандартное форматирование
          return formatDateTime(value, false, currentLocale);
        }
      } catch (error) {
        console.error('Ошибка форматирования метки оси X:', error);
        return value;
      }
    };
  }, [xAxisDateFormat, xAxisDateFormatType, dateFromUTC, currentLocale]);
  
  // Функция форматирования подсказок с датами
  const formatTooltipLabel = useMemo(() => {
    if (!xAxisDateFormat) return undefined;
    
    return (label: string): string => {
      if (!label) return label;
      
      try {
        if (dateFromUTC) {
          // Используем умное форматирование для подсказок
          return formatSmartDate(label, true, currentLocale);
        } else {
          // Для дат не в UTC формате используем полное форматирование с временем
          return formatDateTime(label, true, currentLocale);
        }
      } catch (error) {
        console.error('Ошибка форматирования метки подсказки:', error);
        return label;
      }
    };
  }, [xAxisDateFormat, dateFromUTC, currentLocale]);
  
  // Функция для генерации уникальных ключей для элементов
  const generateUniqueKey = useCallback((entry: Record<string, any>, cellIndex: number, serieIndex: number): string => {
    // Используем id из данных, если доступно и useCustomIds=true
    if (useCustomIds && entry.id) {
      return `${entry.id}-${serieIndex}`;
    }
    
    // Для числовых значений с плавающей точкой применяем округление, чтобы избежать дублирования из-за неточности
    const roundValue = (val: number): number => {
      return Math.round(val * 100) / 100;
    };
    
    // Для значений value/name создаем более стабильные ключи
    const value = typeof entry.value === 'number' ? roundValue(entry.value) : entry.value;
    const name = entry.name || '';
    
    // Составляем уникальный ключ из имени, значения и индексов
    return `${name}-${value}-${cellIndex}-${serieIndex}`;
  }, [useCustomIds]);
  
  // Обработка видимых/скрытых серий
  const visibleSeries = useMemo(() => 
    series.filter(serie => serie.hidden !== true),
  [series]);
  
  // Определяем, нет ли данных
  const hasNoData = useMemo(() => {
    return noData || !data || data.length === 0 || visibleSeries.length === 0;
  }, [noData, data, visibleSeries]);
  
  // Обработка клика на легенду
  const handleLegendClick = useMemo(() => {
    if (!onSeriesToggle) return undefined;
    return (entry: any) => {
      onSeriesToggle(entry.dataKey, !entry.inactive);
    };
  }, [onSeriesToggle]);
  
  return (
    <ChartRenderer
      size={size}
      minHeight={effectiveMinHeight}
      aspectRatio={aspectRatio}
      className={className}
      isLoading={isLoading}
      error={error}
      noData={hasNoData}
      title={title}
      subtitle={subtitle}
      ref={ref}
    >
      <ResponsiveContainer width="100%" height="100%">
        <RechartsBarChart
          data={data}
          layout={layout}
          margin={{ top: 20, right: 30, bottom: 65, left: 40 }}
          barSize={barSize}
          onClick={onClick}
        >
          {/* Сетка */}
          {showGrid && (
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke={COLORS.secondary}
              horizontal={true}
              vertical={true}
            />
          )}
          
          {/* Ось X */}
          <XAxis 
            dataKey={layout === 'vertical' ? undefined : xAxisKey}
            type={layout === 'vertical' ? 'number' : 'category'}
            tick={{ fill: COLORS.text, fontSize: 12 }} 
            tickFormatter={xAxisDateFormat && layout !== 'vertical' ? formatXAxisTick : undefined}
            tickLine={xAxisTickLine}
            axisLine={{ strokeWidth: 1 }}
            padding={{ left: 10, right: 10 }}
            label={xAxisLabel ? { 
              value: xAxisLabel, 
              position: 'insideBottom', 
              offset: -5,
              style: { textAnchor: 'middle' }
            } : undefined}
          />
          
          {/* Ось Y */}
          <YAxis 
            dataKey={layout === 'vertical' ? xAxisKey : undefined}
            type={layout === 'vertical' ? 'category' : 'number'}
            tick={{ fill: COLORS.text, fontSize: 12 }} 
            tickFormatter={xAxisDateFormat && layout === 'vertical' ? formatXAxisTick : undefined}
            tickLine={yAxisTickLine}
            axisLine={{ strokeWidth: 1 }}
            padding={{ top: 10, bottom: 10 }}
            label={yAxisLabel ? { 
              value: yAxisLabel, 
              angle: -90, 
              position: 'insideLeft',
              style: { textAnchor: 'middle' }
            } : undefined}
          />
          
          {/* Тултип */}
          <Tooltip
            content={
              <CustomTooltip 
                valueType={valueType} 
                precision={precision} 
                formatter={tooltipFormatter}
                labelFormatter={formatTooltipLabel}
              />
            }
          />
          
          {/* Легенда */}
          {showLegend && (
            <Legend 
              onClick={handleLegendClick}
              verticalAlign="top"
              wrapperStyle={{ paddingBottom: '10px' }}
            />
          )}
          
          {/* Столбцы/Полосы для каждой серии */}
          {visibleSeries.map((serie, serieIndex) => (
            colorsByItem && serie.itemColors && serie.itemColors.length > 0 ? (
              // Вариант с Cell для индивидуальных цветов
              <Bar
                key={`serie-${serieIndex}-${serie.dataKey}`}
                dataKey={serie.dataKey}
                name={serie.name}
                fill={serie.color || defaultColors[serieIndex % defaultColors.length]}
                stackId={serie.stackId}
                radius={borderRadius ? [borderRadius, borderRadius, 0, 0] : undefined}
                onClick={onBarClick}
              >
                {data.map((entry, cellIndex) => (
                  <Cell
                    key={generateUniqueKey(entry, cellIndex, serieIndex)}
                    fill={serie.itemColors && cellIndex < serie.itemColors.length 
                      ? serie.itemColors[cellIndex] 
                      : defaultColors[cellIndex % defaultColors.length]}
                  />
                ))}
              </Bar>
            ) : (
              // Стандартный вариант с одним цветом для всей серии
              <Bar
                key={`serie-${serieIndex}-${serie.dataKey}`}
                dataKey={serie.dataKey}
                name={serie.name}
                fill={serie.color || defaultColors[serieIndex % defaultColors.length]}
                stackId={serie.stackId}
                radius={borderRadius ? [borderRadius, borderRadius, 0, 0] : undefined}
                onClick={onBarClick}
              >
                {/* Добавляем явные Cell с уникальными ключами для всех элементов */}
                {data.map((entry, cellIndex) => (
                  <Cell
                    key={generateUniqueKey(entry, cellIndex, serieIndex)}
                    fill={serie.color || defaultColors[serieIndex % defaultColors.length]}
                  />
                ))}
              </Bar>
            )
          ))}
        </RechartsBarChart>
      </ResponsiveContainer>
    </ChartRenderer>
  );
});

BarChart.displayName = 'BarChart';

export default BarChart;